import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
    useRef,
  } from "react";
  import { useAppDispatch, useAppSelector } from "../../../store/hooks";
  import { RootState } from "../../../store/store";
  import {
    getFileAClaimFormFieldsJson,
    PostFileAClaim,
  } from "../../../store/user/claims/claimsActions";
  import DynamicForm from "../../../common/DynamicForm/DynamicForm";
  import {
    IAsyncOptionFn,
    IAsyncValueFn,
    IDynamicFormFieldsJson,
    IGetOptionsResponse,
    IOption,
  } from "../../../models/user/DynamicForm/DynamicForm";
  import { CustomAction } from "../../../store/customAction";
  import {
    API_GET_LOOKUP_VALUES,
    API_POST_POLICIES,
    API_POST_POLICY_DETAILS_HEADER,
  } from "../../../Apis/ApiEndPoints";
  import {
    IGetPolicesResponse,
    IPolicy,
  } from "../../../models/user/dashboard/Dashboard";
  import { IPolicyDetailsHeaderResponse } from "../../../models/user/policyDetails/PolicyDetails";
  import { IPostFileAClaimRequestParams } from "../../../models/user/claims/claims";
  import {
    setFileAClaimFormFieldsJson,
    setFileAClaimResponse,
  } from "../../../store/user/claims/claimsSlice";
  import { Api } from "../../..";
  import Acknowledgement from "../../../components/Acknowledgement/Acknowledgement";
  import { GetPoliciesData } from "../../Dashboard/DashboardAction";
  import { GetPolicyDetailsHeader, GetPolicyRisks } from "../../../store/user/policyDetails/policyDetailsActions";
  import { setPolicyDetailsHeader } from "../../../store/user/policyDetails/policyDetailsSlice";
  import dayjs from "dayjs";
  import { useTranslation } from "react-i18next";
  import { IField } from "../../../models/user/DynamicForm/DynamicForm";
  import { Button } from "@mui/material";
  
  interface FileAClaimFormProps {
    policyId: string;
    lobCode: string;
    carrier: string;
    system: string;
    policyNumber?: string;
    onAction?: (action: any) => void;
  }
  
  // Add the loss cause constants
  const PROPERTY_LOSS_CAUSES = [
    { label: "Fire", code: "fire" },
    { label: "Lightning", code: "lightning" },
    { label: "Windstorm", code: "windstorm" },
    { label: "Hail", code: "hail" },
    { label: "Explosion", code: "explosion" },
    { label: "Equipment Breakdown", code: "equipment_breakdown" },
    { label: "Vandalism", code: "vandalism" },
    { label: "Theft", code: "theft" },
    { label: "Water Damage", code: "water_damage" },
    { label: "Smoke", code: "smoke" },
    { label: "Freezing", code: "freezing" },
    { label: "Collapse", code: "collapse" },
    { label: "Mold", code: "mold" },
    { label: "Other", code: "other" },
  ];
  
  const LIABILITY_LOSS_CAUSES = [
    { label: "Slip and Fall", code: "slip_and_fall" },
    { label: "Third-party", code: "third_party" },
    { label: "Product Liability", code: "product_liability" },
    { label: "Libel", code: "libel" },
    { label: "Slander", code: "slander" },
    { label: "Copyright Infringement", code: "copyright_infringement" },
    { label: "Medical Payment", code: "medical_payment" },
  ];
  
  const FileAClaimForm: React.FC<FileAClaimFormProps> = ({
    policyId,
    lobCode,
    carrier,
    system,
    policyNumber,
    onAction,
  }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const hasTriggeredFollowupRef = useRef(false);
  
    const { policyDetailsHeader, policyVehicles, policyDrivers, liability } = useAppSelector(
      (state) => state.policyDetails
    );
  
    const { policiesData } = useAppSelector((state) => state.dashboard);
    const { userDetails } = useAppSelector((state) => state.userAuth);
    const { fileAClaimFormFieldsJson, fileAClaimResponse } = useAppSelector(
      (state: RootState) => state.claims
    );
  
    const [formFieldsJson, setFormFieldsJson] =
      useState<IDynamicFormFieldsJson | null>(null);
    const [incidentTypes, setIncidentTypes] = useState<IOption[]>([]);
  
    // Field dependencies configuration for DynamicForm
    const fieldDependencies = useMemo(
      () => ({
        riskLocation: {
          dependencies: ["incidentSameAsRisk", "incidentLocation"],
          logic: (changedField: string, value: any, allFields: IField[]) => {
            const toggleField = allFields.find(
              (f) => f.name === "incidentSameAsRisk"
            );
            const incidentLocationField = allFields.find(
              (f) => f.name === "incidentLocation"
            );
            const sourceFieldThatChanged = allFields.find(
              (f) => f.name === changedField
            );
  
            if (value === "other") {
              // Turn off toggle when "Other Location" is selected
              if (toggleField) {
                toggleField.value = false;
                toggleField.disabled = true;
              }
  
              // Clear and enable incident location field
              if (incidentLocationField) {
                incidentLocationField.value = "";
                incidentLocationField.disabled = false;
              }
            } else if (value && value !== "other") {
              // Enable toggle when specific location is selected
              if (toggleField) {
                toggleField.disabled = false;
              }
  
              // If toggle is on, populate incident location
              if (
                toggleField &&
                toggleField.value === true &&
                incidentLocationField
              ) {
                const selectedOption = sourceFieldThatChanged?.options?.find(
                  (option) => option.code === value
                );
                if (selectedOption) {
                  incidentLocationField.value = selectedOption.label;
                  incidentLocationField.disabled = true;
                }
              }
            } else {
              // No location selected, disable toggle
              if (toggleField) {
                toggleField.value = false;
                toggleField.disabled = true;
              }
              
              // Clear and enable incident location field
              if (incidentLocationField) {
                incidentLocationField.value = "";
                incidentLocationField.disabled = false;
              }
            }
          },
        },
        incidentSameAsRisk: {
          dependencies: ["incidentLocation", "riskLocation"],
          logic: (changedField: string, value: any, allFields: IField[]) => {
            const incidentLocationField = allFields.find(
              (f) => f.name === "incidentLocation"
            );
            const riskLocationField = allFields.find(
              (f) => f.name === "riskLocation"
            );
  
            if (incidentLocationField) {
              if (value === true) {
                // Toggle is ON
                if (
                  riskLocationField &&
                  riskLocationField.value &&
                  riskLocationField.value !== "other"
                ) {
                  const selectedOption = riskLocationField.options?.find(
                    (option) => option.code === riskLocationField.value
                  );
                  if (selectedOption) {
                    incidentLocationField.value = selectedOption.label;
                    incidentLocationField.disabled = true;
                  }
                } else {
                  // If "Other Location" is selected or no location selected, clear and enable
                  incidentLocationField.value = "";
                  incidentLocationField.disabled = false;
                }
              } else {
                // Toggle is OFF
                incidentLocationField.value = "";
                incidentLocationField.disabled = false;
              }
            }
          },
        },
        incidentLocation: {
          dependencies: ["incidentSameAsRisk", "riskLocation"],
          logic: (changedField: string, value: any, allFields: IField[]) => {
            const incidentLocationField = allFields.find(
              (f) => f.name === "incidentLocation"
            );
            const incidentSameAsRiskField = allFields.find(
              (f) => f.name === "incidentSameAsRisk"
            );
            const riskLocationField = allFields.find(
              (f) => f.name === "riskLocation"
            );
  
            if (incidentLocationField) {
              // Always ensure incidentLocation is a text field
              incidentLocationField.type = "text";
  
              if (
                incidentSameAsRiskField &&
                incidentSameAsRiskField.value === true
              ) {
                // When toggle is ON, check if "Other Location" is selected
                if (riskLocationField && riskLocationField.value === "other") {
                  // If "Other Location" is selected, make it editable even when toggle is ON
                  incidentLocationField.disabled = false;
                } else {
                  // When toggle is ON and specific location is selected, make incident location read-only
                  incidentLocationField.disabled = true;
                }
              } else {
                // When toggle is OFF, make it editable
                incidentLocationField.disabled = false;
              }
            }
          },
        },
        lossDate: {
          dependencies: ["reportedDate"],
          logic: (changedField: string, value: any, allFields: IField[]) => {
            const reportedDateField = allFields.find((f) => f.name === "reportedDate");
            const lossDateField = allFields.find((f) => f.name === "lossDate");
            if (lossDateField) {
              // Validate against effectiveDate and reportedDate
              const effective = policyDetailsHeader?.effectiveDate ? dayjs(policyDetailsHeader.effectiveDate) : null;
              const loss = value ? (dayjs.isDayjs(value) ? value : dayjs(value)) : null;
              const reported = reportedDateField?.value ? (dayjs.isDayjs(reportedDateField.value) ? reportedDateField.value : dayjs(reportedDateField.value)) : null;
              
              if (loss && effective && loss.isBefore(effective, "day")) {
                lossDateField.error = `${lossDateField.label} has an invalid date.`;
              }
              if (loss && reported && loss.isAfter(reported, "day")) {
                lossDateField.error = "Future dates are not allowed";
              }
            }
          },
        },
        // Add the new exposure field dependency
        exposure: {
          dependencies: ["lossCause", "riskLocation"],
          logic: (changedField: string, value: any, allFields: IField[]) => {
            const lossCauseField = allFields.find((f) => f.name === "lossCause");
            const riskLocationField = allFields.find(
              (f) => f.name === "riskLocation"
            );
  
            if (lossCauseField) {
              if (value === "Liability") {
                lossCauseField.options = LIABILITY_LOSS_CAUSES;
                lossCauseField.value = "";
              } else if (value === "Property") {
                lossCauseField.options = PROPERTY_LOSS_CAUSES;
                lossCauseField.value = "";
              } else {
                lossCauseField.options = [];
                lossCauseField.value = "";
              }
            }
  
            // Update risk location options based on exposure
            if (
              riskLocationField &&
              liability &&
              Object.keys(liability).length > 0
            ) {
              const options: any[] = [];
  
              if (
                value === "Property" &&
                liability &&
                "propertyList" in liability &&
                liability.propertyList
              ) {
                liability?.propertyList?.forEach(
                  (property: any, index: number) => {
                    if (property.address) {
                      let label =
                        property.address.addr1 ||
                        `Property Location ${index + 1}`;
  
                      // Add address information
                      if (property.address.addr1) {
                        label = property.address.addr1;
                        if (property.address.city) {
                          label += `, ${property.address.city}`;
                        }
                        if (property.address.stateProvCd) {
                          label += `, ${property.address.stateProvCd}`;
                        }
                      }
  
                      options.push({
                        label: label,
                        code: `property_${index}`,
                      });
                    }
                  }
                );
              } else if (
                value === "Liability" &&
                liability &&
                "liabilityList" in liability &&
                liability.liabilityList
              ) {
                liability?.liabilityList.forEach(
                  (liability: any, index: number) => {
                    if (liability.address) {
                      let label =
                        liability.address.addr1 ||
                        `Liability Location ${index + 1}`;
  
                      // Add address information
                      if (liability.address.addr1) {
                        label = liability.address.addr1;
                        if (liability.address.city) {
                          label += `, ${liability.address.city}`;
                        }
                        if (liability.address.stateProvCd) {
                          label += `, ${liability.address.stateProvCd}`;
                        }
                      }
  
                      options.push({
                        label: label,
                        code: `liability_${index}`,
                      });
                    }
                  }
                );
              }
  
              // Add "Other Location" option
              options.push({
                label: "Other Location",
                code: "other",
              });
  
              riskLocationField.options = options;
              riskLocationField.value = ""; // Clear the value when exposure changes
            }
          },
        },
      }),
      [liability, policyDetailsHeader]
    );
  
    const handleFieldChange = useCallback((fieldName: string, value: any) => {
      // This function can be used for logging or additional custom logic if needed
      console.log(`Field changed: ${fieldName} = ${value}`);
    }, []);
  
    const getPoliciesOptions = useCallback(
      ({
        policies,
        policyType,
      }: {
        policies: IPolicy[];
        policyType: string;
      }): IOption[] =>
        policies
          .filter((policy) => {
            if (policyId) {
              if (
                policy.policyId === policyId &&
                policy.policyType === policyType
              ) {
                return true;
              }
            } else if (policy.policyType === policyType) {
              return true;
            }
            return false;
          })
          .map((policy) => ({
            code: `${policy.policyId}`,
            label: policy.carrierPolicyNo,
          })),
      [policyId]
    );
  
    const asyncOptionsFunctions: { [key: string]: IAsyncOptionFn } = useMemo(
      () => ({
        getPolicyTypes: async (fieldName) => {
          try {
            const response: IGetOptionsResponse = await CustomAction(
              Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
              dispatch,
              "getPolicyTypes"
            );
            return response.data;
          } catch (error) {
            return [];
          }
        },
        getPolicies: async (policyType) => {
          try {
            if (policiesData && policiesData.policyList) {
              const options: IOption[] = getPoliciesOptions({
                policies: policiesData.policyList,
                policyType: policyType ? policyType : "",
              });
              return options;
            } else {
              if (userDetails) {
                const response: IGetPolicesResponse = await CustomAction(
                  Api.post(API_POST_POLICIES, {
                    customerId: userDetails?.customerId,
                    carrier,
                    system,
                  }),
                  dispatch,
                  "getPolicies"
                );
                if (response.data && response.data.policyList) {
                  const options: IOption[] = getPoliciesOptions({
                    policies: response.data.policyList,
                    policyType: policyType ? policyType : "",
                  });
                  return options;
                }
              }
            }
            return [];
          } catch (error) {
            return [];
          }
        },
        getVehicles: async (policyId) => {
          try {
            const response: IPolicyDetailsHeaderResponse = await CustomAction(
              Api.post(API_POST_POLICY_DETAILS_HEADER, {
                policyId,
              }),
              dispatch,
              "getVehicles"
            );
            if (response && response.data && response.data.riskList) {
              const options: IOption[] = response.data.riskList.map((risk) => ({
                code: risk.riskId,
                label: risk.description,
              }));
              return options;
            }
            return [];
          } catch (error) {
            return [];
          }
        },
        getLocationTypes: async (fieldName) => {
          try {
            const response: IGetOptionsResponse = await CustomAction(
              Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
              dispatch,
              "getLocationTypes"
            );
            return response.data;
          } catch (error) {
            return [];
          }
        },
        getExposures: async (fieldName) => {
          try {
            const response: IGetOptionsResponse = await CustomAction(
              Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
              dispatch,
              "getExposures"
            );
            return response.data;
          } catch (error) {
            return [];
          }
        },
        getIncidentTypes: async (fieldName) => {
          try {
            const response: IGetOptionsResponse = await CustomAction(
              Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
              dispatch,
              "getIncidentTypes"
            );
            setIncidentTypes(response.data);
            return response.data;
          } catch (error) {
            return [];
          }
        },
      }),
      [dispatch, policiesData, userDetails, getPoliciesOptions, carrier, system]
    );
  
    const asyncValueFunctions: { [key: string]: IAsyncValueFn } = useMemo(
      () => ({
        getPrimaryResidenceLocation: async (locationType) => {
          if (locationType === "otherLocation") {
            return "";
          }
          return policyDetailsHeader &&
            policyDetailsHeader.lobCode === "HO" &&
            policyDetailsHeader.riskList.length > 0 &&
            policyDetailsHeader.riskList[0].address
            ? `${policyDetailsHeader.riskList[0].address.addr1} ${policyDetailsHeader.riskList[0].address.city} ${policyDetailsHeader.riskList[0].address.county} ${policyDetailsHeader.riskList[0].address.postalCode} ${policyDetailsHeader.riskList[0].address.regionCd} ${policyDetailsHeader.riskList[0].address.stateProvCd}`
            : "";
        },
      }),
      [policyDetailsHeader]
    );
  
    const getCarrierPolicyNo = useCallback(
      ({ policyId }: { policyId: string }) =>
        policiesData?.policyList?.find((policy) => policy.policyId === policyId)
          ?.carrierPolicyNo || "",
      [policiesData]
    );
  
    const getEventCode = useCallback(
      ({ templateId }: { templateId: string }): string => {
        return (
          incidentTypes.find((incidentType) => incidentType.code === templateId)
            ?.label || ""
        );
      },
      [incidentTypes]
    );
  
    const handleSubmit = useCallback(
      async (formValues: { [key: string]: any }) => {
        try {
          if (userDetails && userDetails.customerId) {
            // Ensure we have at least some basic values
            if (!formValues || typeof formValues !== 'object') {
              return;
            }
            const requestBody: IPostFileAClaimRequestParams = {
              address: {
                addr1: "202 Morningside Ave",
                addrTypeCd: "InsuredAddr",
                city: "Madison",
                county: "Wisconsin",
                postalCode: "53716-1731",
                regionCd: "United States",
                regionISOCd: "US",
                stateProvCd: "WI",
              },
              carrier,
              customerId: userDetails?.customerId,
              description: formValues.incidentDesc || formValues.description || formValues.incidentDescription || "",
              detailDescription: formValues.incidentDesc || formValues.description || formValues.incidentDescription || "",
              eventCode: getEventCode({ templateId: formValues.templateId || formValues.incidentType || "" }),
              lobCode,
              lossDate: (formValues.reportedDate && typeof formValues.reportedDate.format === 'function' ? formValues.reportedDate.format("YYYY-MM-DD") : "") || 
                        (formValues.lossDate && typeof formValues.lossDate.format === 'function' ? formValues.lossDate.format("YYYY-MM-DD") : "") || 
                        (formValues.incidentDate && typeof formValues.incidentDate.format === 'function' ? formValues.incidentDate.format("YYYY-MM-DD") : "") || 
                        dayjs().format("YYYY-MM-DD"),
              lossTm: (formValues.reportedTime && typeof formValues.reportedTime.format === 'function' ? formValues.reportedTime.format("HH:mm") : "") || 
                      (formValues.incidentTime && typeof formValues.incidentTime.format === 'function' ? formValues.incidentTime.format("HH:mm") : "") || 
                      dayjs().format("HH:mm"),
              carrierPolicyNo: getCarrierPolicyNo({
                policyId: formValues.policyId || "",
              }),
              riskId: formValues.riskId || "",
              sourceCd: "WebPortal",
              system,
              templateIdRef: formValues.templateId || formValues.incidentType || "",
            };
            dispatch(PostFileAClaim(requestBody));
          }
        } catch (error: any) {
          // Error handled silently
        }
      },
      [
        userDetails,
        getCarrierPolicyNo,
        getEventCode,
        dispatch,
        carrier,
        lobCode,
        system,
      ]
    );
  
  const handleCancel = useCallback(() => {
    if (onAction) {
      const pn = policyNumber || getCarrierPolicyNo({ policyId: policyId || "" }) || policyDetailsHeader?.carrierPolicyNumber;
      if (pn) {
        onAction({ followup_message: `Show me details for policy ${pn}` });
      } else {
        onAction({ followup_message: "Show policy summary" });
      }
    }
  }, [onAction, policyNumber, getCarrierPolicyNo, policyId, policyDetailsHeader?.carrierPolicyNumber]);
  
    useEffect(() => {
      if (lobCode) {
        dispatch(setPolicyDetailsHeader(null));
        dispatch(setFileAClaimResponse(null)); // Reset previous response
        dispatch(getFileAClaimFormFieldsJson(lobCode?.toLowerCase()));
      }
    }, [dispatch, lobCode, policyId]); // Include policyId to reset when policy changes
  
    // Cleanup effect to reset state when component unmounts
    useEffect(() => {
      return () => {
        dispatch(setFileAClaimResponse(null));
      };
    }, []); // Empty dependency array - only runs on mount/unmount
  
  // Trigger a follow-up question after successful claim filing
  useEffect(() => {
    if (fileAClaimResponse && onAction && !hasTriggeredFollowupRef.current) {
      hasTriggeredFollowupRef.current = true;
      const pn = policyNumber || getCarrierPolicyNo({ policyId: policyId || "" });
      if (pn) {
        onAction({ followup_message: `Show policy summary` });
      } else {
        onAction({ followup_message: "Show me my recent claim status" });
      }
    }
  }, [fileAClaimResponse, onAction, policyNumber, getCarrierPolicyNo, policyId]);

    useEffect(() => {
      if (
        !policiesData &&
        carrier &&
        system &&
        userDetails &&
        userDetails.customerId
      ) {
        dispatch(
          GetPoliciesData({
            carrier: carrier,
            system: system,
            customerId: userDetails?.customerId,
          })
        );
      }
    }, [policiesData, carrier, system, userDetails, dispatch]);
  
    useEffect(() => {
      if (!policyDetailsHeader && carrier && system && lobCode && policyId) {
        dispatch(GetPolicyDetailsHeader({ carrier, system, lobCode, policyId }));
      }
    }, [policyDetailsHeader, carrier, system, lobCode, policyId, dispatch]);
  
    // Add a new useEffect to handle liability data loading
    useEffect(() => {
      if (carrier && system && lobCode && policyId) {
        dispatch(GetPolicyRisks({ carrier, system, lobCode, policyId }));
      }
    }, [carrier, system, lobCode, policyId, dispatch]);
  
    // Modify the existing useEffect to also depend on liability data being loaded
    useEffect(() => {
      if (fileAClaimFormFieldsJson && liability) {
        setFormFieldsJson({
          ...fileAClaimFormFieldsJson,
          sections: fileAClaimFormFieldsJson?.sections?.map((section) => ({
            ...section,
            fields: section.fields.map((field) => {
              let updatedField = { ...field };
  
              // Handle incidentLocation field to make it a text field
              if (updatedField.name === "incidentLocation") {
                updatedField.type = "text";
              }
  
              // Set initial state for incidentSameAsRisk toggle - initially disabled
              if(updatedField.name === "incidentSameAsRisk"){
                updatedField.disabled = true;
                updatedField.value = false;
              }
  
              // Set default value for lossDate (Reported Date)
              if (updatedField.name === "reportedDate") {
                if (!updatedField.value) {
                  updatedField.value = dayjs();
                } else if (!dayjs.isDayjs(updatedField.value)) {
                  updatedField.value = dayjs(updatedField.value);
                }
              }
              // Set default value for incidentTime (Reported Time)
              if (updatedField.name === "reportedTime") {
                if (!updatedField.value) {
                  updatedField.value = dayjs();
                } else if (!dayjs.isDayjs(updatedField.value)) {
                  updatedField.value = dayjs(updatedField.value);
                }
                // Do not allow selecting future time
                updatedField.disableFuture = true;
              }
              // Ensure other time fields (if present) also do not allow future times
              if (
                updatedField.type === "time" &&
                (updatedField.name === "incidentTime" ||
                  updatedField.name === "lossTime")
              ) {
                updatedField.disableFuture = true;
              }
              if (policyDetailsHeader && policyId) {
                if (updatedField.name === "policyType") {
                  updatedField.value = policyDetailsHeader.policyType;
                  updatedField.disabled = true;
                }
  
                if (updatedField.name === "policyId") {
                  updatedField.options = [
                    {
                    label: policyNumber || "",
                      code: policyId,
                    },
                  ];
                  updatedField.value = policyId;
                  updatedField.disabled = true;
                }
                if (updatedField.name === "policyNumber") {
                  updatedField.value = policyDetailsHeader.carrierPolicyNumber;
                  updatedField.disabled = true;
                }
                if (
                  updatedField.name === "riskId" &&
                  policyDetailsHeader.policyType === "PersonalAuto"
                ) {
                  updatedField.options = policyDetailsHeader.riskList.map(
                    (risk) => ({
                      label: risk.description,
                      code: risk.riskId,
                    })
                  );
                  updatedField.disabled = false;
                  updatedField.hidden = false;
                }
              }
              if (
                updatedField.name === "lossDate" &&
                updatedField.type === "date"
              ) {
                updatedField.minDate = policyDetailsHeader?.effectiveDate || "";
              }
              if (updatedField.name === "riskLocation") {
                // Get risk locations from the liability data
                const riskLocations = (liability as any)?.riskLocations || [];
                (updatedField as any).type = "select"; // Use select type for dropdown
  
                const options: any[] = [];
  
                if (riskLocations && riskLocations.length > 0) {
                  riskLocations.forEach((location: any, index: number) => {
                    // Create a descriptive label for each location
                    let label =
                      location.locationDescription ||
                      `Location ${location.locationNumber}`;
  
                    // Add address information if available
                    if (location.address && location.address.addr1) {
                      label += ` - ${location.address.addr1}`;
                      if (location.address.city) {
                        label += `, ${location.address.city}`;
                      }
                      if (location.address.stateProvCd) {
                        label += `, ${location.address.stateProvCd}`;
                      }
                    }
  
                    options.push({
                      label: label,
                      code: `location_${location.locationNumber}`,
                    });
                  });
                }
  
                // Add "Other Location" option
                options.push({
                  label: "Other Location",
                  code: "other",
                });
  
                updatedField.options = options;
                updatedField.disabled = false;
                updatedField.hidden = false;
              }
              return updatedField;
            }),
          })),
        });
      }
    }, [fileAClaimFormFieldsJson, policyDetailsHeader, policyId, liability]);
  
    if (fileAClaimResponse) {
      return <Acknowledgement />;
    }
  
  if (!formFieldsJson) {
    return <div>Loading form...</div>;
  }

    return (
      <DynamicForm
        formFieldsJson={formFieldsJson}
        onFormSubmit={handleSubmit}
        asyncOptionsFunctions={asyncOptionsFunctions}
        asyncValueFunctions={asyncValueFunctions}
        isFileAClaim={lobCode}
        onFieldChange={handleFieldChange}
        fieldDependencies={fieldDependencies}
        rightActions={
          <Button variant="contained" color="primary" onClick={handleCancel}>
            {t("Cancel")}
          </Button>
        }
      />
    );
  };
  
  export default FileAClaimForm;
  